import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, IconButton, InputAdornment, Link as MuiLink, Paper, Stack, TextField, Typography } from '@mui/material';
import { ArrowForward, BusinessOutlined, LockOutlined, MailOutline, Visibility, VisibilityOff } from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiRequest, clearApiSessionToken, getApiSessionToken, setActiveOrganizationId } from '../Utils/apiClient';
import dataunfoldLogo from '../images/logo-7-80x80.png';

let welcomeClaimSessionToken = '';
let welcomeClaimPromise = null;

export const afterSaasLoginPath = async () => {
  if (sessionStorage.getItem('dataunfold.pendingInvitation')) return '/accept-invitation';
  if (sessionStorage.getItem('dataunfold.invitedOrganizationId')) return '/organization';
  const sessionToken = getApiSessionToken();
  if (!sessionToken) return Promise.resolve('/');
  if (sessionToken !== welcomeClaimSessionToken) {
    welcomeClaimSessionToken = sessionToken;
    welcomeClaimPromise = apiRequest('/api/v1/saas/welcome/claim', { method: 'POST' })
      .then(result => result.show_welcome === true ? '/welcome' : '/')
      .catch(() => '/');
  }
  return welcomeClaimPromise;
};

const SaasAccessPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const mode = location.pathname === '/signup' ? 'signup' : location.pathname === '/verify-email' ? 'verify' : 'invite';
  const token = new URLSearchParams(location.search).get('token') || (mode === 'invite' ? sessionStorage.getItem('dataunfold.pendingInvitation') : '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState('');
  const [invitation, setInvitation] = useState(null);
  const [enabled, setEnabled] = useState(null);
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(true);
  const [busy, setBusy] = useState(false);
  const [accountExists, setAccountExists] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Keep email secrets out of subsequent request referrers and browser history.
    if (token && new URLSearchParams(location.search).has('token')) {
      window.history.replaceState(null, '', location.pathname);
    }
  }, [token, location.pathname, location.search]);

  useEffect(() => {
    let active = true;
    if (mode === 'signup') {
      apiRequest('/api/v1/saas/config').then(result => {
        if (active) {
          setEnabled(result.enabled);
          setEmailVerificationRequired(result.email_verification_required !== false);
        }
      }).catch(err => active && setError(err.message));
    }
    if (mode === 'invite') {
      apiRequest('/api/v1/saas/invitation', { method: 'POST', body: JSON.stringify({ token }) })
        .then(result => active && setInvitation(result))
        .catch(err => { if (active) { setError(err.message); sessionStorage.removeItem('dataunfold.pendingInvitation'); } });
    }
    return () => { active = false; };
  }, [mode, token]);

  const submit = async event => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    setAccountExists(false);
    try {
      const path = mode === 'signup' ? 'signup' : mode === 'verify' ? 'verify-email' : 'accept-invitation';
      const body = mode === 'signup' ? { email, password, organization_name: name } : { token, password: password || undefined };
      const result = await apiRequest(`/api/v1/saas/${path}`, { method: 'POST', body: JSON.stringify(body) });
      setMessage(t(`saas.${result.status}`));
      if (mode === 'signup') setEmailVerificationRequired(result.email_verification_required !== false);
      if (mode !== 'signup' || result.email_verification_required === false) {
        sessionStorage.removeItem('dataunfold.pendingInvitation');
        if (mode === 'invite') {
          sessionStorage.removeItem('dataunfold.saasWelcome');
          sessionStorage.setItem('dataunfold.invitedOrganizationId', String(result.organization_id));
        } else {
          sessionStorage.removeItem('dataunfold.invitedOrganizationId');
        }
        window.history.replaceState(null, '', location.pathname);
        if (mode === 'invite' && invitation?.account_exists) {
          await apiRequest(`/api/v1/organizations/${result.organization_id}/switch`, { method: 'POST' });
          setActiveOrganizationId(result.organization_id);
          window.location.href = '/organization';
        }
      }
      setPassword('');
    } catch (err) {
      if (mode === 'signup' && err.code === 'account_exists') {
        setAccountExists(true);
        setPassword('');
      } else {
        setError(err.message);
      }
    }
    finally { setBusy(false); }
  };

  const signIn = event => {
    event.preventDefault();
    if (mode === 'invite' && !message) sessionStorage.setItem('dataunfold.pendingInvitation', token);
    clearApiSessionToken();
    window.location.href = '/login';
  };

  const resend = async () => {
    setBusy(true);
    try {
      await apiRequest('/api/v1/saas/resend-verification', { method: 'POST', body: JSON.stringify({ email }) });
      setMessage(t('saas.verification_sent'));
      setError('');
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  if (mode === 'signup') {
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 3 },
        background: 'radial-gradient(ellipse at 100% 0%, rgba(255, 224, 207, 0.6), transparent 42%), radial-gradient(ellipse at 0% 100%, rgba(218, 225, 255, 0.65), transparent 48%), #f7f8fc',
      }}>
        <Paper component="form" onSubmit={submit} elevation={0} sx={{
          p: { xs: 3, sm: 5 },
          maxWidth: 468,
          width: '100%',
          borderRadius: 3,
          border: '1px solid rgba(255, 255, 255, 0.9)',
          boxShadow: '0 20px 60px rgba(33, 44, 79, 0.08)',
          backgroundColor: 'rgba(255, 255, 255, 0.94)',
        }}>
          <Stack spacing={2.25}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 0.5 }}>
              <Box component="img" src={dataunfoldLogo} alt="" aria-hidden="true" sx={{ width: 30, height: 30 }} />
              <Typography sx={{ fontWeight: 700, fontSize: 17, letterSpacing: '-0.035em', color: '#202738' }}>DataUnfold</Typography>
            </Stack>
            <Box sx={{ textAlign: 'center', mb: 0.5 }}>
              <Typography variant="h4" sx={{ fontSize: { xs: 23, sm: 25 }, fontWeight: 700, letterSpacing: '-0.035em', color: '#202431' }}>
                {t('saas.signup_title')}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.75, fontSize: 14, lineHeight: 1.5 }}>
                {t(emailVerificationRequired ? 'saas.signup_description' : 'saas.signup_description_no_verification')}
              </Typography>
            </Box>
            {error && <Alert severity="error">{error}</Alert>}
            {accountExists && <Alert severity="info">{t('saas.account_exists')}</Alert>}
            {message && <Alert severity="success">{message}</Alert>}
            {enabled === false && <Alert severity="info">{t('saas.disabled')}</Alert>}
            {!message && <>
              <TextField
                label={t('saas.organization_name')}
                required
                fullWidth
                value={name}
                onChange={e => setName(e.target.value)}
                inputProps={{ maxLength: 120 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><BusinessOutlined fontSize="small" /></InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, '& .MuiInputAdornment-root': { color: '#7f8798' } }}
              />
              <TextField
                label={t('saas.email')}
                type="email"
                autoComplete="email"
                required
                fullWidth
                value={email}
                onChange={e => { setEmail(e.target.value); setAccountExists(false); setError(''); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><MailOutline fontSize="small" /></InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, '& .MuiInputAdornment-root': { color: '#7f8798' } }}
              />
              <TextField
                label={t('saas.password')}
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                fullWidth
                value={password}
                onChange={e => setPassword(e.target.value)}
                helperText={t('saas.password_help')}
                inputProps={{ minLength: 12 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LockOutlined fontSize="small" /></InputAdornment>,
                  endAdornment: <InputAdornment position="end"><IconButton
                    aria-label={t(showPassword ? 'saas.hide_password' : 'saas.show_password')}
                    onClick={() => setShowPassword(visible => !visible)}
                    edge="end"
                    size="small"
                  >{showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment>,
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, '& .MuiInputAdornment-root': { color: '#7f8798' }, '& .MuiFormHelperText-root': { mx: 1.5 } }}
              />
            </>}
            {!message && <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={busy || accountExists || enabled !== true}
              endIcon={!busy && <ArrowForward />}
              sx={{
                minHeight: 48,
                mt: 0.25,
                borderRadius: 2,
                fontWeight: 700,
                background: 'linear-gradient(100deg, #5053b9 0%, #4c4fb3 100%)',
                '&:hover': { background: 'linear-gradient(100deg, #4548aa 0%, #4144a4 100%)' },
              }}
            >
              {busy ? <CircularProgress size={22} color="inherit" /> : t('saas.signup_submit')}
            </Button>}
            {email && emailVerificationRequired && <Button disabled={busy} onClick={resend} size="small">{t('saas.resend')}</Button>}
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', pt: 0.25, fontSize: 13 }}>
              {accountExists ? <MuiLink component={Link} to="/login" onClick={signIn} underline="hover">{t('saas.sign_in_existing')}</MuiLink> : <>
                {t('saas.signup_have_account')}{' '}
                <MuiLink component={Link} to="/login" onClick={signIn} underline="hover" sx={{ fontWeight: 600 }}>{t('saas.login')}</MuiLink>
              </>}
            </Typography>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f5fb', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper component="form" onSubmit={submit} sx={{ p: { xs: 3, sm: 5 }, maxWidth: 480, width: '100%', borderRadius: 4 }}>
        <Stack spacing={3}>
          <Typography variant="overline" color="primary">DataUnfold</Typography>
          <Typography variant="h4">{t(`saas.${mode}_title`)}</Typography>
          <Typography color="text.secondary">{t(mode === 'signup' && !emailVerificationRequired ? 'saas.signup_description_no_verification' : `saas.${mode}_description`)}</Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {accountExists && <Alert severity="info">{t('saas.account_exists')}</Alert>}
          {message && <Alert severity="success">{message}</Alert>}
          {mode === 'signup' && enabled === false && <Alert severity="info">{t('saas.disabled')}</Alert>}
          {!message && mode === 'signup' && <>
            <TextField label={t('saas.organization_name')} required value={name} onChange={e => setName(e.target.value)} inputProps={{ maxLength: 120 }} />
            <TextField label={t('saas.email')} type="email" autoComplete="email" required value={email} onChange={e => { setEmail(e.target.value); setAccountExists(false); setError(''); }} />
            <TextField label={t('saas.password')} type="password" autoComplete="new-password" required value={password} onChange={e => setPassword(e.target.value)} helperText={t('saas.password_help')} inputProps={{ minLength: 12 }} />
          </>}
          {mode === 'invite' && invitation && !message && <>
            <Alert severity="info">{t('saas.invited_to', { name: invitation.name, email: invitation.email, role: t(`saas.role_${invitation.role}`) })}</Alert>
            {invitation.account_exists ? <Button component={Link} to="/login" onClick={signIn}>{t('saas.sign_in_invited')}</Button>
              : <TextField label={t('saas.password')} type="password" autoComplete="new-password" required value={password} onChange={e => setPassword(e.target.value)} helperText={t('saas.password_help')} inputProps={{ minLength: 12 }} />}
          </>}
          {!message && <Button type="submit" variant="contained" size="large" disabled={busy || accountExists || (mode === 'signup' && enabled !== true) || (mode === 'invite' && !invitation)}>
            {busy ? <CircularProgress size={22} color="inherit" /> : t(`saas.${mode}_submit`)}
          </Button>}
          {mode === 'signup' && email && emailVerificationRequired && <Button disabled={busy} onClick={resend}>{t('saas.resend')}</Button>}
          <Button component={Link} to="/login" onClick={signIn} variant={accountExists ? 'contained' : 'text'}>
            {t(accountExists ? 'saas.sign_in_existing' : 'saas.login')}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default SaasAccessPage;
